import Step from '@material-ui/core/Step';
import StepButton from '@material-ui/core/StepButton';
import Stepper from '@material-ui/core/Stepper';
import withStyles from '@material-ui/core/styles/withStyles';
import Card from 'components/Card/Card.jsx';
import CardBody from 'components/Card/CardBody.jsx';
import CardFooter from 'components/Card/CardFooter.jsx';
import Button from 'components/CustomButtons/Button';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import SnackbarContent from 'components/Snackbar/SnackbarContent';
import Space from 'components/Space/Space';
import { default as React, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RenderQuestion from "views/Survey/RenderQuestions/RenderQuestion";
import Toast from "views/Toast/Toast";
import classNames from "classnames";
import { slideInLeft, slideInRight } from 'react-animations'
import { makeStyles } from '@material-ui/core';

const useStyles = makeStyles((theme) => {
    return {
        root: {
            overflowX: 'scroll'
        },
        stepBtn: {
            width: '10rem',
            '& text': {
                fill: 'white'
            }
        },
        activeStepBtn: {
            '& .MuiStepLabel-label.MuiStepLabel-active': {
                color: '#07009f',
                fontWeight: 'bolder'
            }
        },
        alignRight: {
            textAlign: 'right',
            // On a phone the title column is full-width, so right alignment made even short
            // questions hug the right edge and wrap oddly; left-aligned and a size down, a short
            // question fits on one line above its options.
            '@media (max-width: 600px)': {
                textAlign: 'left',
                // The theme's light heading weight leaks into the <b> at this size, so the bold is
                // stated outright.
                '& h3, & h3 b': {
                    fontSize: 16,
                    margin: '6px 0',
                    fontWeight: 700,
                }
            }
        },
        previousButton: {
            [theme.breakpoints.down('sm')]: {
                textAlign: 'left',
            },
            [theme.breakpoints.up('md')]: {
                textAlign: 'right',
            },
        },
        '@keyframes slideInLeft': slideInLeft,
        '@keyframes slideInRight': slideInRight,
        animateLeft: {
            animationName: '$slideInLeft',
            animationDuration: '0.5s',
        },
        animateRight: {
            animationName: '$slideInRight',
            animationDuration: '0.5s',
        },
    }
})

// Chrome's scroll anchoring reverts programmatic scrolls around content swaps: measured on a real
// phone with ?debugscroll=1 — our correction moved the page by +457 and 28ms later it sat 12px from
// where it started, then an identical correction 400ms later stuck. Re-enabling anchoring "to let
// Chrome do its job" was tried and made exactly that bounce. It stays off while answering.
function disableScrollAnchoring() {
    if (typeof document === 'undefined') return () => { }
    const b = document.body, h = document.documentElement
    const prevB = b ? b.style.overflowAnchor : ''
    const prevH = h ? h.style.overflowAnchor : ''
    if (b) b.style.overflowAnchor = 'none'
    if (h) h.style.overflowAnchor = 'none'
    return () => {
        if (b) b.style.overflowAnchor = prevB
        if (h) h.style.overflowAnchor = prevH
    }
}

// Only one child of the questions block is displayed at a time; that is the one the guest has to
// answer, and the only one worth measuring.
function visibleQuestionEl(wrap) {
    if (!wrap || !wrap.children) return null
    for (let i = 0; i < wrap.children.length; i++) {
        const c = wrap.children[i]
        if (c && c.style && c.style.display !== 'none') return c
    }
    return null
}

// The element that actually scrolls is not necessarily the window — this layout nests the survey
// inside its own scrolling container on some routes.
function scrollParentOf(el) {
    let n = el && el.parentElement
    while (n && n !== document.body && n !== document.documentElement) {
        const s = window.getComputedStyle(n)
        if (/(auto|scroll)/.test(s.overflowY) && n.scrollHeight > n.clientHeight + 1) return n
        n = n.parentElement
    }
    return null
}

// Bring the current question on screen, and do nothing else.
//
// Every richer version of this has cost more than it bought. Pinning the sections card pushed the
// question below the fold on a phone; scrolling six times across 600ms swallowed taps as gestures;
// and scrollIntoView moves EVERY scrollable ancestor including the window, which in Chrome threw the
// guest back up to the hotel logo after each answer while Firefox stayed put. Falling back to the
// survey root when the question could not be measured made that jump certain rather than likely.
//
// So the adjustment is relative: measure the displayed question and, when it needs moving, scroll by
// exactly the difference. A relative scroll cannot land on the top of the page whatever the
// surrounding layout does — that is the whole point of doing it this way.
function ensureQuestionVisible(questionWrap, restoreY) {
    if (typeof window === 'undefined') return
    // The next question occupies exactly the place the answered one held (hidden siblings take no
    // space), so the RIGHT position is wherever the guest already was: `restoreY` is the page
    // position at the moment they tapped, and the early passes simply put it back if the browser
    // moved it. Only the late passes measure — and only step in if the question is unusable there.
    const restore = () => {
        try {
            if (restoreY == null) return
            const y = window.pageYOffset || document.documentElement.scrollTop || 0
            if (Math.abs(y - restoreY) <= 30) return
            window.scrollTo(0, restoreY)
        } catch (e) { /* best effort */ }
    }
    const run = () => {
        try {
            const q = visibleQuestionEl(questionWrap)
            if (!q || typeof q.getBoundingClientRect !== 'function') return
            const vh = window.innerHeight || document.documentElement.clientHeight || 0
            if (!vh) return
            const r = q.getBoundingClientRect()
            // Answering a question changes the page's content and Chrome moves the view on its own —
            // on a phone it lands near the top of the page, leaving the next question dangling at the
            // bottom edge. This correction is the counter-measure, not the cause: with it removed the
            // guest had to scroll down again after every single answer.
            //
            // "Wholly out of view" was too weak a test, because after that jump the question is
            // technically still on screen — a title and one option, with nothing reachable. And
            // "must start near the top" was too strong: scrolling down to reach the lower options
            // puts the title above the viewport quite legitimately, and correcting that yanked the
            // guest back up.
            //
            // So: act when the question starts too low to be usable, or when so little of it is left
            // below the fold that there is nothing to tap. Anything between those is the guest's own
            // scroll position and is left alone.
            if (r.top <= vh * 0.45 && r.bottom >= vh * 0.25) return
            const delta = Math.round(r.top - vh * 0.12)   // a little context left above the question
            if (!delta) return
            const sp = scrollParentOf(q)
            if (sp) sp.scrollTop += delta
            else window.scrollBy(0, delta)
        } catch (e) { /* best effort — never break answering over a scroll */ }
    }
    // Restore passes first — all within ~200ms of the tap, before the guest could be scrolling
    // themselves — then the measuring passes for whatever position that left us in. Every pass is a
    // no-op when the page is already where it should be.
    const first = (restoreY != null) ? restore : run
    if (window.requestAnimationFrame) window.requestAnimationFrame(first); else setTimeout(first, 16)
    setTimeout(restore, 80)
    setTimeout(restore, 200)
    setTimeout(run, 320)
    setTimeout(run, 520)
}

// Does this survey use branching at all? Only surveys with at least one logic rule (or a
// logic default) use the path-driven engine below; everything else — i.e. every existing
// production survey — keeps the original linear engine untouched.
function hasBranchingLogic(questions) {
    if (!questions || !questions.sections) return false
    return questions.sections.some(section =>
        (section.questions || []).some(q =>
            q && q.logic && ((Array.isArray(q.logic.rules) && q.logic.rules.length > 0) || q.logic.default)
        )
    )
}

// ---- Section visibility (conditional sections) ------------------------------
// A section can carry an optional display condition tied to an earlier "gate" question, e.g. a
// multi-select "Which outlets did you use?" — the Brasserie section shows only when that answer
// includes "Brasserie". Shape: section.visibility = { questionId, op: 'includes'|'nincludes',
// values: [optionValue, ...] }. No condition ⇒ the section always shows (legacy behaviour).
function answerEntryId(e) { return e ? (e.answer ? e.answer.id : e.id) : undefined }
function answerEntryVal(e) { return e ? (e.answer ? e.answer.value : e.value) : undefined }

// Does a visibility condition match the answers so far? No condition ⇒ always visible.
// `answersArr` is the engine's answers list ({ answer:{id,value} }).
function visibilityMatches(v, answersArr) {
    if (!v || !v.questionId || !Array.isArray(v.values) || v.values.length === 0) return true
    const entry = (answersArr || []).find(a => answerEntryId(a) === v.questionId)
    const raw = answerEntryVal(entry)
    const arr = Array.isArray(raw) ? raw : (raw == null || raw === '' ? [] : [raw])
    const wanted = v.values.map(String)
    const intersects = arr.map(String).some(x => wanted.indexOf(x) >= 0)
    return v.op === 'nincludes' ? !intersects : intersects
}

// Is a section shown? Honours the manual display switch first, then its visibility condition.
function sectionVisible(section, answersArr) {
    if (!section) return false
    if (section.display === false) return false
    return visibilityMatches(section.visibility, answersArr)
}

// Is a question shown? (per-question conditional visibility, e.g. "Age of child 2" only when the
// earlier "number of children" answer includes 2). No condition ⇒ always shown.
function questionVisible(q, answersArr) {
    return visibilityMatches(q && q.visibility, answersArr)
}

// Does an answer actually carry something the guest chose? An empty string, null/undefined or an
// empty array all mean "not answered" — a stored 0 is a real value and must count as answered.
function answerHasValue(v) {
    if (v === null || v === undefined || v === '') return false
    if (Array.isArray(v)) return v.length > 0
    return true
}

// Every question component announces itself once it has mounted (`isInit`), carrying an EMPTY
// answer. RadioButtonView does it on a 100ms timer *and* from its mount-time closure, so on a phone
// — where that timer can slip behind the guest's taps while the browser is busy laying out and
// scrolling the new section — the announcement arrives AFTER the guest has already answered and
// silently overwrites the real answer with nothing. That is how sections ended up stored with only
// their last question filled in: the earlier taps were wiped by their own late init announcements.
// An init announcement may only ever create an entry or refresh an empty one — it must never erase
// a value. Guarding it here covers every question type, not just radio.
function initWouldEraseAnswer(existing, incoming) {
    return !answerHasValue(incoming && incoming.answer ? incoming.answer.value : null)
        && answerHasValue(existing && existing.answer ? existing.answer.value : null)
}

// ---- Branching helpers ------------------------------------------------------
// Strict numeric parse: only a fully-numeric string is a number, so date strings like
// "2026-08-01" aren't misread as the year 2026 and hijack numeric comparison.
const toNum = (x) => {
    if (x == null) return null
    if (typeof x === 'number') return isNaN(x) ? null : x
    const s = String(x).trim()
    return /^-?\d+(\.\d+)?$/.test(s) ? parseFloat(s) : null
}
function evaluateRule(op, aVal, rVal, rVal2) {
    if (op === 'includes' || op === 'nincludes') {
        const arr = Array.isArray(aVal) ? aVal : (aVal == null ? [] : [aVal])
        const has = arr.map(String).indexOf(String(rVal)) >= 0
        return op === 'includes' ? has : !has
    }
    if (op === 'contains' || op === 'ncontains') {
        const has = String(aVal == null ? '' : aVal).toLowerCase()
            .indexOf(String(rVal == null ? '' : rVal).toLowerCase()) >= 0
        return op === 'contains' ? has : !has
    }
    const an = toNum(aVal), rn = toNum(rVal), rn2 = toNum(rVal2)
    const bothNum = an !== null && rn !== null
    const ad = Date.parse(aVal), rd = Date.parse(rVal), rd2 = Date.parse(rVal2)
    const bothDate = !isNaN(ad) && !isNaN(rd)
    switch (op) {
        case 'eq': return bothNum ? an === rn : String(aVal) === String(rVal)
        case 'ne': return bothNum ? an !== rn : String(aVal) !== String(rVal)
        case 'gt': return bothNum ? an > rn : (bothDate ? ad > rd : false)
        case 'gte': return bothNum ? an >= rn : (bothDate ? ad >= rd : false)
        case 'lt': return bothNum ? an < rn : (bothDate ? ad < rd : false)
        case 'lte': return bothNum ? an <= rn : (bothDate ? ad <= rd : false)
        case 'between':
            if (bothNum && rn2 !== null) return an >= Math.min(rn, rn2) && an <= Math.max(rn, rn2)
            if (bothDate && !isNaN(rd2)) return ad >= Math.min(rd, rd2) && ad <= Math.max(rd, rd2)
            return false
        default: return false
    }
}

// ---- Linear engine (unchanged original behaviour, used when there is no branching) ----
function LinearFlow(props) {
    const { i18n } = props;
    const { t } = useTranslation();
    const [questions, setQuestions] = useState(props.questions)
    const [activeStep, setActiveStep] = useState(0)
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
    const [slideDirection, setSlideDirection] = useState('in')
    const [refreshCount, setRefreshCount] = useState(0)
    const answers = useRef([])
    const questionsRef = useRef(null)
    const rootRef = useRef(null)
    const didMountRef = useRef(false)
    // Page position at the moment the guest last interacted — the position every scroll correction
    // tries to give back to them (see ensureQuestionVisible).
    const holdScrollY = useRef(null)
    const rememberScroll = () => {
        try { holdScrollY.current = window.pageYOffset || document.documentElement.scrollTop || 0 }
        catch (e) { holdScrollY.current = null }
    }

    const classes = useStyles()

    useEffect(() => {
        init()
    }, [props.doRefresh])

    useEffect(() => {
        const stepContainer = document.getElementById('stepContainer')
        const stepElement = document.getElementById('step' + activeStep)

        stepContainer.scroll(stepElement.offsetLeft - stepContainer.clientWidth / 2 + stepElement.clientWidth / 2, 0)
    }, [activeStep])

    // When the survey auto-advances to the next question/section, pin the sections card to the top
    // (sections above, current question below) instead of letting the page jump up into the hotel
    // intro. Skipped on the first render so the guest still sees the intro when the survey loads.
    // Answering swaps question content in place; without this Chrome's anchoring reverts the
    // scroll restoration measured above. See disableScrollAnchoring.
    useEffect(() => { return disableScrollAnchoring() }, [])

    useEffect(() => {
        if (!didMountRef.current) { didMountRef.current = true; return }
        const y = holdScrollY.current
        holdScrollY.current = null
        ensureQuestionVisible(questionsRef.current, y)
    }, [currentQuestionIndex, activeStep])

    // Conditional sections can shrink the visible list as the gate answer changes; keep the active
    // step index within range so navigation never points at a now-hidden section.
    useEffect(() => {
        const n = getActiveSections().length
        if (n > 0 && activeStep >= n) setActiveStep(n - 1)
    }, [refreshCount, activeStep])

    // Likewise, conditional questions can shrink the visible questions of the current section;
    // keep currentQuestionIndex in range so it never points at a now-hidden question.
    //
    // The pointer belongs to ONE section, so the section it was last aligned to is tracked here.
    // Moving on sets the new section and resets the pointer, but those are two separate state
    // updates: for one render the newly mounted section is already on screen while the pointer still
    // holds the position the guest reached in the PREVIOUS one. Clamping that leftover value found
    // it out of range and "corrected" it to this section's LAST question — so every question before
    // it was never displayed, and came back empty. That is what silently emptied the first question
    // of every section shorter than the one before it (RECEPTION among them) from 17 Aug onwards.
    // Aligning the pointer to the section first, and only then clamping, keeps the conditional-
    // question behaviour while making the reset authoritative.
    const pointerSection = useRef(activeStep)
    useEffect(() => {
        if (pointerSection.current !== activeStep) {
            pointerSection.current = activeStep
            if (currentQuestionIndex !== 0) setCurrentQuestionIndex(0)
            return
        }
        const sec = getActiveSections()[activeStep]
        if (!sec) return
        const vq = sec.questions.filter(q => questionVisible(q, answers.current))
        if (currentQuestionIndex > 0 && currentQuestionIndex >= vq.length) setCurrentQuestionIndex(Math.max(0, vq.length - 1))
    }, [refreshCount, activeStep, currentQuestionIndex])

    const init = () => {
        setQuestions(props.questions)
        setActiveStep(0)
        setCurrentQuestionIndex(0)
        answers.current = []
    }
    const handleNext = () => {
        updateActiveStep(activeStep + 1);
    }

    const handleBack = () => {
        updateActiveStep(activeStep - 1);
    }
    const totalSteps = () => {
        return getActiveSections().length
    }
    const handleReset = () => {
        updateActiveStep(0);
    }
    const isValidStep = (step) => {
        const activeSections = getActiveSections()[step]
        if (!activeSections) return true
        const visibleQs = activeSections.questions.filter(q => questionVisible(q, answers.current))
        let invalidAnswerIndex = visibleQs.findIndex((qitem) => {
            const answer = getAnswerByID(qitem.id)
            if (qitem.required) {
                return !answer || !answer.isValid
            } else {
                return false
            }
        })
        return invalidAnswerIndex === -1
    }
    const updateActiveStep = (step) => {
        rememberScroll()
        if (activeStep < step) {
            if (isValidStep(activeStep)) {
                if (step === totalSteps()) {
                    // The stepper can jump over sections, so walking-forward validation alone lets
                    // required questions go unanswered. The submit is the one gate everybody passes:
                    // re-check every visible section and take the guest to the first incomplete one
                    // instead of silently accepting a partial response.
                    const firstInvalid = getActiveSections().findIndex((sec, i) => !isValidStep(i))
                    if (firstInvalid >= 0) {
                        Toast(400, t('Please answer all required questions to continue'))
                        setActiveStep(firstInvalid)
                        setCurrentQuestionIndex(0)
                        return
                    }
                    // Submit only what belongs to the sections and questions visible under the
                    // FINAL answers. A guest who answered one conditional branch and then changed
                    // the gate answer leaves the abandoned branch's picks behind in answers.current;
                    // sending those too would count one respondent in two mutually exclusive
                    // branches of the reports.
                    const visibleIds = {}
                    getActiveSections().forEach((sec) =>
                        sec.questions.filter(q => questionVisible(q, answers.current))
                            .forEach(q => { visibleIds[q.id] = true }))
                    props.saveAnswer(answers.current.filter(item => visibleIds[item.answer.id]).map((item) => {
                        const { isValid, ...answer } = item
                        return answer.answer
                    }))
                } else {
                    setActiveStep(step)
                    setCurrentQuestionIndex(0)
                }
            } else {
                Toast(400, t('Please answer all required questions to continue'))
            }
        } else {
            setActiveStep(step)
            setCurrentQuestionIndex(0)
        }
    }
    const getActiveSections = () => {
        // Sections are filtered by the manual display switch AND any conditional-visibility rule
        // evaluated against the answers so far (e.g. only the outlets the guest said they used).
        const activeSections = questions.sections.filter((section) => sectionVisible(section, answers.current))
        return activeSections
    }
    const getAnswerByID = (id) => {
        const answer = answers.current.filter(item => item.answer.id === id)[0]
        if (answer) {
            return answer;
        } else {
            return null
        }
    }
    const changeAnswer = (data, isInit) => {
        let currentAnswers = [...answers.current];
        const existIndex = currentAnswers.findIndex(item => item.answer.id === data.answer.id);
        if (existIndex === -1) {
            currentAnswers.push(data)
        } else {
            // A late "I have mounted" announcement must never erase what the guest already picked.
            if (isInit && initWouldEraseAnswer(currentAnswers[existIndex], data)) return
            currentAnswers[existIndex] = data
        }
        answers.current = (currentAnswers)
        if (!isInit) rememberScroll()   // a real tap: keep the page here through the swap
        setRefreshCount((c) => (c + 1))
    }
    const onPrevQuestion = () => {
        if (currentQuestionIndex > 0) {
            rememberScroll()
            setCurrentQuestionIndex(currentQuestionIndex - 1)
            setSlideDirection('left')
        }
    }
    const onNextQuestion = () => {
        rememberScroll()
        setCurrentQuestionIndex(currentQuestionIndex + 1)
        setSlideDirection('right')
    }
    const getStepContent = (sectionIndex) => {
        const activeSections = getActiveSections()[sectionIndex]
        if (!activeSections) return null   // step may briefly point past a section a gate answer just hid
        // Only the questions whose conditional-visibility rule matches the answers so far, so e.g.
        // "Age of child 2" appears only when the earlier count answer includes 2.
        const visibleQs = activeSections.questions.filter(q => questionVisible(q, answers.current))
        const questionCount = visibleQs.length
        return visibleQs.map((qitem, key) => {
            const title = qitem.required ? '*' + qitem.questionTitle : qitem.questionTitle
            const answer = getAnswerByID(qitem.id);
            const isLastQuestion = key == (questionCount - 1)
            const isFirstQuestion = key == 0
            const idActiveQuestion = key == currentQuestionIndex
            const hideNextButton = (qitem.required && (!answer || !answer.isValid)) || isLastQuestion
            const isRadioQuestion = qitem.question.type == 'radio'
            const isCheckBoxRequireOnlyOneAnswer = qitem.question.type == 'checkbox' && qitem.question.answertype == 'one'
            const slideClass = slideDirection == 'left' ? classes.animateLeft : classes.animateRight

            return <div key={key} style={{ display: idActiveQuestion ? 'block' : 'none' }}>
                <GridContainer key={key} className={slideClass}>
                    <GridItem xs={12} md={5} sm={5} className={classes.alignRight}>
                        <h3><b>{title}</b></h3>
                    </GridItem>
                    <GridItem xs={12} md={7} sm={7}>
                        <RenderQuestion
                            key={questions.language + qitem.id}
                            question={qitem}
                            answer={answer ? answer.answer : null}
                            onChange={(answer, isInit) => { // isInit when question component upload data after mounted
                                if (isInit) {
                                    changeAnswer(answer, true)
                                } else {
                                    changeAnswer(answer)
                                    if (isRadioQuestion || isCheckBoxRequireOnlyOneAnswer) {
                                        // Advance AFTER a double-click would have landed: an
                                        // immediate swap put the second click of a double-click on
                                        // the NEXT question's buttons (same screen position), and
                                        // the old 100ms section hop validated mid-double-click.
                                        // 400ms also lets the guest see their choice register.
                                        if (!isLastQuestion) {
                                            setTimeout(() => { onNextQuestion() }, 400)
                                        } else {
                                            if ((activeStep + 1) < totalSteps()) {
                                                setTimeout(() => {
                                                    updateActiveStep(activeStep + 1)
                                                }, 400);
                                            }
                                        }
                                    }
                                }
                            }} />
                    </GridItem>
                </GridContainer>

                <GridContainer>
                    <GridItem xs={6} md={5} sm={5} className={classes.previousButton}>
                        {!isFirstQuestion && <Button style={{ padding: 6 }} simple color='info' onClick={onPrevQuestion}>{t('Previous Question')}</Button>}

                    </GridItem>
                    <GridItem xs={6} md={7} sm={7} align='right'>
                        {!hideNextButton && <Button style={{ padding: 6, }} simple color='info' onClick={onNextQuestion}>{t('Next Question')}</Button>}
                    </GridItem>
                </GridContainer>
                <hr />
            </div>
        })
    }

    if (questions && questions.sections.length > 0) {

        const activeSections = getActiveSections()
        return <div ref={rootRef}>
            <Card>
                <CardBody>
                    <Stepper alternativeLabel nonLinear activeStep={activeStep} className={classes.root} id="stepContainer">
                        {activeSections.map((section, index) => {
                            const stepProps = {};
                            const buttonProps = {};
                            const isActive = activeStep == index
                            const stepButtonClasses = classNames({
                                [classes.stepBtn]: true,
                                [classes.activeStepBtn]: isActive
                            })
                            return (
                                <Step key={index} {...stepProps} id={'step' + index}>
                                    <StepButton
                                        className={stepButtonClasses}
                                        onClick={() => updateActiveStep(index)}
                                        {...buttonProps}
                                        completed={false}
                                    >
                                        {section.sectionTitle}
                                    </StepButton>
                                </Step>
                            );
                        })}
                    </Stepper>
                    <Space height={"2rem"} />

                    <div ref={questionsRef}>
                        {getStepContent(activeStep)}
                    </div>

                </CardBody>
                <CardFooter>
                    <Button disabled={activeStep === 0} onClick={handleBack} style={{ padding: 6 }}>
                        {t('Previous Section')}
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleNext}
                        style={{ padding: 6 }}
                    >
                        {(activeStep + 1) === totalSteps() ? t('Complete') : t('Next Section')}
                    </Button>
                </CardFooter>
            </Card>
        </div>
    } else {
        return <div>
            <SnackbarContent
                fullWidth
                message={t('No data')}
                color="info"
            />
        </div>
    }
}

// ---- Branching engine (path-driven; used only when the survey has logic rules) ----
function BranchingFlow(props) {
    const { i18n } = props;
    const { t } = useTranslation();
    const [questions, setQuestions] = useState(props.questions)
    const [pointer, setPointer] = useState(0)           // index into the flattened question list
    const [history, setHistory] = useState([])          // visited pointers, for "Previous"
    const [slideDirection, setSlideDirection] = useState('in')
    const [refreshCount, setRefreshCount] = useState(0)
    const answers = useRef([])
    const questionsRef = useRef(null)
    const rootRef = useRef(null)
    const didMountRef = useRef(false)
    // Page position at the moment the guest last interacted — the position every scroll correction
    // tries to give back to them (see ensureQuestionVisible).
    const holdScrollY = useRef(null)
    const rememberScroll = () => {
        try { holdScrollY.current = window.pageYOffset || document.documentElement.scrollTop || 0 }
        catch (e) { holdScrollY.current = null }
    }

    const classes = useStyles()

    // Same anti-scroll-anchoring guard as the linear engine (see LinearFlow).

    // Flatten every question of every displayed section into one ordered list. Branching jumps
    // (question / section / END) navigate over this list; ids map back to positions.
    const getActiveSections = () => (questions && questions.sections
        ? questions.sections.filter((section) => sectionVisible(section, answers.current)) : [])
    const activeSections = getActiveSections()
    const flat = []
    const idToIndex = {}
    activeSections.forEach((section, si) => {
        (section.questions || []).forEach((q) => {
            if (!questionVisible(q, answers.current)) return   // per-question conditional visibility
            idToIndex[q.id] = flat.length; flat.push({ sectionIndex: si, question: q });
        })
    })
    const currentEntry = flat[pointer] || null
    const currentSection = currentEntry ? currentEntry.sectionIndex : 0

    useEffect(() => {
        init()
    }, [props.doRefresh])

    useEffect(() => {
        const stepContainer = document.getElementById('stepContainer')
        if (!stepContainer) return
        const stepElement = document.getElementById('step' + currentSection)
        if (stepElement) {
            stepContainer.scroll(stepElement.offsetLeft - stepContainer.clientWidth / 2 + stepElement.clientWidth / 2, 0)
        }
    }, [currentSection])

    // Pin the sections card to the top (sections above, current question below) when the flow
    // advances/goes back, instead of jumping up into the hotel intro.
    // Answering swaps question content in place; without this Chrome's anchoring reverts the
    // scroll restoration measured above. See disableScrollAnchoring.
    useEffect(() => { return disableScrollAnchoring() }, [])

    useEffect(() => {
        if (!didMountRef.current) { didMountRef.current = true; return }
        const y = holdScrollY.current
        holdScrollY.current = null
        ensureQuestionVisible(questionsRef.current, y)
    }, [pointer])

    const init = () => {
        setQuestions(props.questions)
        setPointer(0)
        setHistory([])
        answers.current = []
    }

    const getAnswerByID = (id) => answers.current.filter(item => item.answer.id === id)[0] || null

    const changeAnswer = (data, isInit) => {
        let currentAnswers = [...answers.current];
        const existIndex = currentAnswers.findIndex(item => item.answer.id === data.answer.id);
        if (existIndex === -1) currentAnswers.push(data)
        else {
            // Same guard as the linear engine: a late mount announcement must not wipe an answer.
            if (isInit && initWouldEraseAnswer(currentAnswers[existIndex], data)) return
            currentAnswers[existIndex] = data
        }
        answers.current = currentAnswers
        if (!isInit) rememberScroll()   // a real tap: keep the page here through the swap
        setRefreshCount((c) => (c + 1))
    }

    // Resolve a branching target to a flat pointer. '' / unknown => next question; 'END' => -1.
    const resolveTarget = (target) => {
        if (!target) return pointer + 1
        if (target === 'END') return -1
        if (target.indexOf('q:') === 0) {
            const id = target.slice(2)
            return (idToIndex[id] != null) ? idToIndex[id] : pointer + 1
        }
        if (target.indexOf('section:') === 0) {
            const si = parseInt(target.slice(8), 10)
            const idx = flat.findIndex(e => e.sectionIndex === si)
            return idx >= 0 ? idx : pointer + 1
        }
        return pointer + 1
    }

    // Where does the flow go after the given question, given its answer and logic rules?
    const computeNextPointer = (entry) => {
        const q = entry.question
        const ans = getAnswerByID(q.id)
        const val = ans ? ans.answer.value : null
        const logic = q.logic
        if (logic && Array.isArray(logic.rules) && logic.rules.length) {
            for (const rule of logic.rules) {
                if (!rule || rule.target === undefined) continue
                if (evaluateRule(rule.op, val, rule.value, rule.value2)) return resolveTarget(rule.target)
            }
            if (logic.default) return resolveTarget(logic.default)
        }
        return pointer + 1
    }

    // A question must be answered before advancing if it is marked required OR if it drives
    // branching (its rules decide the path, so an unanswered branch question would fall through
    // linearly into a follow-up that was only meant for a specific answer).
    const answerRequired = (entry) => {
        if (!entry) return false
        const q = entry.question
        const hasRules = q.logic && Array.isArray(q.logic.rules) && q.logic.rules.length > 0
        return !!q.required || !!hasRules
    }
    const isRequiredUnanswered = (entry) => {
        if (!answerRequired(entry)) return false
        const a = getAnswerByID(entry.question.id)
        return !a || !a.isValid
    }

    const finish = () => {
        const visitedIds = new Set([...history, pointer].map(p => (flat[p] ? flat[p].question.id : null)))
        const payload = answers.current
            .filter(item => visitedIds.has(item.answer.id))
            .map(item => item.answer)
        props.saveAnswer(payload)
    }

    const goNext = () => {
        const entry = flat[pointer]
        if (!entry) return
        if (isRequiredUnanswered(entry)) { Toast(400, t('Please answer all required questions to continue')); return }
        let next = computeNextPointer(entry)
        // Loop guard: a rule that targets its own question would stall the flow — advance instead.
        if (next === pointer) next = pointer + 1
        if (next === -1 || next >= flat.length) { finish(); return }
        rememberScroll()
        setHistory(h => [...h, pointer])
        setSlideDirection('right')
        setPointer(next)
    }

    const goPrev = () => {
        if (!history.length) return
        const prev = history[history.length - 1]
        rememberScroll()
        setHistory(h => h.slice(0, -1))
        setSlideDirection('left')
        setPointer(prev)
    }

    // Manual section navigation from the stepper: jump to that section's first question.
    const jumpToSection = (si) => {
        const idx = flat.findIndex(e => e.sectionIndex === si)
        if (idx >= 0 && idx !== pointer) {
            rememberScroll()
            setHistory(h => [...h, pointer])
            setSlideDirection(idx > pointer ? 'right' : 'left')
            setPointer(idx)
        }
    }

    const renderCurrent = () => {
        if (!currentEntry) return null
        const qitem = currentEntry.question
        const title = qitem.required ? '*' + qitem.questionTitle : qitem.questionTitle
        const answer = getAnswerByID(qitem.id)
        const isRadioQuestion = qitem.question.type == 'radio'
        const isCheckBoxRequireOnlyOneAnswer = qitem.question.type == 'checkbox' && qitem.question.answertype == 'one'
        const slideClass = slideDirection == 'left' ? classes.animateLeft : classes.animateRight
        return (
            <GridContainer className={slideClass}>
                <GridItem xs={12} md={5} sm={5} className={classes.alignRight}>
                    <h3><b>{title}</b></h3>
                </GridItem>
                <GridItem xs={12} md={7} sm={7}>
                    <RenderQuestion
                        key={questions.language + qitem.id}
                        question={qitem}
                        answer={answer ? answer.answer : null}
                        onChange={(a, isInit) => { // isInit when the question component uploads data after mounting
                            changeAnswer(a, isInit)
                            if (!isInit && (isRadioQuestion || isCheckBoxRequireOnlyOneAnswer)) {
                                // Same double-click protection as the linear engine.
                                setTimeout(() => goNext(), 400)
                            }
                        }}
                    />
                </GridItem>
            </GridContainer>
        )
    }

    if (questions && questions.sections && questions.sections.length > 0 && flat.length > 0) {
        const willComplete = currentEntry
            ? (() => { const n = computeNextPointer(currentEntry); return n === -1 || n >= flat.length })()
            : false
        const requiredUnanswered = isRequiredUnanswered(currentEntry)
        return <div ref={rootRef}>
            <Card>
                <CardBody>
                    <Stepper alternativeLabel nonLinear activeStep={currentSection} className={classes.root} id="stepContainer">
                        {activeSections.map((section, index) => {
                            const stepProps = {};
                            const buttonProps = {};
                            const isActive = currentSection == index
                            const stepButtonClasses = classNames({
                                [classes.stepBtn]: true,
                                [classes.activeStepBtn]: isActive
                            })
                            return (
                                <Step key={index} {...stepProps} id={'step' + index}>
                                    <StepButton
                                        className={stepButtonClasses}
                                        onClick={() => jumpToSection(index)}
                                        {...buttonProps}
                                        completed={false}
                                    >
                                        {section.sectionTitle}
                                    </StepButton>
                                </Step>
                            );
                        })}
                    </Stepper>
                    <Space height={"2rem"} />

                    <div ref={questionsRef}>
                        {renderCurrent()}
                    </div>

                </CardBody>
                <CardFooter>
                    <Button disabled={history.length === 0} onClick={goPrev} style={{ padding: 6 }}>
                        {t('Previous')}
                    </Button>
                    <Button
                        color="primary"
                        disabled={requiredUnanswered}
                        onClick={goNext}
                        style={{ padding: 6 }}
                    >
                        {willComplete ? t('Complete') : t('Next')}
                    </Button>
                </CardFooter>
            </Card>
        </div>
    } else {
        return <div>
            <SnackbarContent
                fullWidth
                message={t('No data')}
                color="info"
            />
        </div>
    }
}

// Dispatcher: existing (no-logic) surveys keep the original engine; only surveys that use
// branching rules get the path-driven engine. The choice is stable for a given survey.
function SurveyProgress(props) {
    return hasBranchingLogic(props.questions)
        ? <BranchingFlow {...props} />
        : <LinearFlow {...props} />
}

export default SurveyProgress;
